import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "immovox_cookie_consent";

type ConsentStatus = "accepted" | "rejected" | null;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const updateGoogleConsent = (granted: boolean) => {
  if (window.gtag) {
    window.gtag("consent", "update", {
      ad_storage: granted ? "granted" : "denied",
      ad_user_data: granted ? "granted" : "denied",
      ad_personalization: granted ? "granted" : "denied",
    });
  }
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentStatus;

    // PostHog fonctionne séparément en mode cookieless.
    // Cette bannière pilote uniquement le consentement Google.
    if (consent) {
      updateGoogleConsent(consent === "accepted");
      return;
    }

    const timer = setTimeout(() => {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 50);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleConsent = (accepted: boolean) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, accepted ? "accepted" : "rejected");
    updateGoogleConsent(accepted);

    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ${
        isAnimating ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
    >
      <div className="container-tight mx-auto">
        <div className="bg-card border border-border rounded-xl shadow-warm-lg p-3 md:p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="hidden md:flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Cookie className="h-6 w-6 text-primary" />
            </div>

            <div className="flex-1">
              <h3 className="hidden md:block text-base font-semibold text-foreground mb-1">
                🍪 Nous utilisons des cookies
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                <Link
                  to="/confidentialite"
                  className="text-primary hover:underline font-medium"
                >
                  En savoir plus
                </Link>
              </p>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleConsent(false)}
                className="flex-1 md:flex-none"
              >
                Refuser
              </Button>
              <Button
                size="sm"
                onClick={() => handleConsent(true)}
                className="flex-1 md:flex-none"
              >
                Accepter
              </Button>
            </div>

            <button
              onClick={() => handleConsent(false)}
              className="absolute top-2 right-2 md:hidden p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
